/**
 * LOT U1b — lisibilité du texte posé sur une couleur de DONNÉE.
 *
 * Les couleurs métier (couleur de groupe `bands.color`, couleur de membre
 * `team_members.color`, patch mark, couleur de statut choisie par le serveur) ne
 * sont PAS des jetons de thème : on ne les remplace pas par `var(--*)`, sinon on
 * réécrit la donnée de l'utilisateur. En revanche le TEXTE posé dessus doit rester
 * lisible dans les deux thèmes : on choisit l'encre par la luminance WCAG de la
 * couleur reçue (noir-ish sur fond clair, blanc sur fond sombre), au lieu
 * d'écrire `#fff` ou `#111` en dur et de parier sur la valeur.
 *
 * `--on-*` (src/index.css) reste la bonne réponse quand le fond est un JETON
 * (--success, --danger…) : ces valeurs-là sont connues et mesurées.
 */

/** Encre sombre du thème Roadcase (identique à `--on-accent`). */
export const INK_DARK = '#141310';
/** Encre claire, pour les fonds réellement sombres. */
export const INK_LIGHT = '#ffffff';

type RGB = { r: number; g: number; b: number };

/** Parse `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb(...)`/`rgba(...)`. null si non reconnu. */
export function parseColor(input: string | null | undefined): RGB | null {
  if (!input) return null;
  const s = input.trim();
  const hex = s.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length === 4) h = h.slice(0, 3).split('').map((c) => c + c).join('');
    if (h.length !== 6 && h.length !== 8) return null;
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }
  const rgb = s.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const p = rgb[1].split(',').map((v) => parseFloat(v));
    if (p.length < 3 || p.some((v) => Number.isNaN(v))) return null;
    return { r: p[0], g: p[1], b: p[2] };
  }
  return null;
}

/** Luminance relative WCAG d'une couleur ; null si la couleur n'est pas reconnue. */
export function relativeLuminance(input: string | null | undefined): number | null {
  const c = parseColor(input);
  if (!c) return null;
  const f = (v: number): number => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
}

/** Ratio de contraste WCAG entre deux couleurs ; null si l'une n'est pas reconnue. */
export function contrastRatio(a: string, b: string): number | null {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Encre lisible sur une couleur de donnée : renvoie celle des deux encres
 * (INK_DARK / INK_LIGHT) qui donne le MEILLEUR ratio. Une couleur non reconnue
 * renvoie `fallback` (le texte du thème) — on ne devine pas.
 */
export function readableInk(color: string | null | undefined, fallback = 'var(--text)'): string {
  const l = relativeLuminance(color);
  if (l === null) return fallback;
  const dark = relativeLuminance(INK_DARK);
  if (dark === null) return fallback;
  // Ratio vs encre sombre / vs encre claire (blanc = luminance 1).
  const rDark = (Math.max(l, dark) + 0.05) / (Math.min(l, dark) + 0.05);
  const rLight = 1.05 / (l + 0.05);
  return rDark >= rLight ? INK_DARK : INK_LIGHT;
}

/**
 * Teinte translucide d'une couleur de donnée (`rgba(r,g,b,alpha)`), pour les
 * chips dont le fond suit la couleur métier. Une couleur non reconnue renvoie
 * `fallback` (un jeton), jamais une valeur inventée.
 */
export function tint(color: string | null | undefined, alpha: number, fallback = 'var(--chip-bg)'): string {
  const c = parseColor(color);
  if (!c) return fallback;
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${alpha})`;
}
